
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAdminEndpointUrl } from '../lib/admin-api-config';
import { adminHttpClient } from '../lib/admin-http-client';

const ADMIN_TOKEN_KEY = 'admin_token';

interface AdminAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string) => Promise<boolean>;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = sessionStorage.getItem(ADMIN_TOKEN_KEY);
    if (token) {
      setIsAuthenticated(true);
    } else if (pathname !== '/admin/login') {
      navigate('/admin/login', { replace: true });
    }
    setIsLoading(false);
  }, [pathname, navigate]);

  const login = useCallback(async (token: string): Promise<boolean> => {
    try {
      // Validar el token intentando listar empresas
      sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
      const url = getAdminEndpointUrl('companies', 'list');
      await adminHttpClient.get(url);
      setIsAuthenticated(true);
      navigate('/admin/empresas', { replace: true });
      return true;
    } catch {
      sessionStorage.removeItem(ADMIN_TOKEN_KEY);
      setIsAuthenticated(false);
      return false;
    }
  }, [navigate]);

  const logout = useCallback(() => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setIsAuthenticated(false);
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  return (
    <AdminAuthContext.Provider value={{ isAuthenticated, isLoading, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
}
